import * as React from 'react';
import {
    Modal,
    ModalHeader,
    ModalTitle,
    ModalBody,
    ModalFooter,
    Button
} from 'react-bootstrap';
import {AssociationScope} from "../../contract-features-3/domain";
import {AutoAssociate, ClearAutoAssociationFeedback} from "../../../actions/actions";
import {FeatureTypeSummary, SkinnyFeatureType} from "../../../../integration/representation/feature-type";
import {AutomaticAssociationParameters, ProximityBasis, AssociationScope as ServerAssociationScope} from "../../../../integration/representation/association";
import {FeatureTypeAttributes} from "../state/AssociationsState";

interface AutoAssociateButtonProps {
    onAutoAssociate: AutoAssociate;
    featureTypes: FeatureTypeSummary[];
    clearAutoAssociationFeedback: ClearAutoAssociationFeedback;
    autoAssociateCount: number;
    tab: AssociationScope;
    associationTypeId: number;
    targetType: FeatureTypeAttributes;
    nvdbSearchDistance: number;
    isFetching: boolean;
}

interface AutoAssociateButtonState {
    showModal: boolean;
    associationType: string;
    parentType: SkinnyFeatureType;
    childType: SkinnyFeatureType;
    nvdbSearchDistance: number;
    proximityType: string;
    parentOptions: SkinnyFeatureType[];
    childOptions: SkinnyFeatureType[];
}


interface ErrorBoundryState {
    hasError: boolean
}

class ErrorBoundary extends React.Component<{}, ErrorBoundryState> {
    constructor(props: {}) {
        super(props);
        this.state = {hasError: false};
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        this.setState({hasError: true});
        console.error(error);
        console.info(info);
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return <h1>Something went wrong.</h1>;
        }
        return this.props.children;
    }
}


export default class AutoAssociateButton extends React.Component<AutoAssociateButtonProps, AutoAssociateButtonState> {

    constructor(props: AutoAssociateButtonProps) {
        super(props);
        this.state = {
            showModal: false,
            associationType: "EMPTY",
            parentType: undefined,
            childType: undefined,
            nvdbSearchDistance: 1,
            proximityType: "GEOMETRY",
            parentOptions: [],
            childOptions: []
        };

        this.handleAssociationTypeChange = this.handleAssociationTypeChange.bind(this);
        this.handleParentTypeChange = this.handleParentTypeChange.bind(this);
        this.handleChildTypeChange = this.handleChildTypeChange.bind(this);
        this.handleMaxDistanceChange = this.handleMaxDistanceChange.bind(this);
        this.handleProximityChange = this.handleProximityChange.bind(this);

    }

    close() {
        this.setState({
            showModal: false,
            associationType: "EMPTY",
            parentType: undefined,
            childType: undefined,
            nvdbSearchDistance: 1,
            proximityType: "GEOMETRY"
        });
        this.props.clearAutoAssociationFeedback();
    }

    open() {
        if (!!this.props.targetType) {
            switch (this.props.tab) {
                case "PROJECT_TO_PROJECT":
                case "PROJECT_TO_NVDB":
                    let parentType = this.props.featureTypes.find((type) => {
                        return type.typeid == this.props.associationTypeId;
                    });
                    this.setState({
                        showModal: true,
                        associationType: this.props.tab,
                        parentType: parentType ? ({typeId: parentType.typeid, name: parentType.name, sortNo: parentType.sortNum}) : undefined,
                        childType: this.props.targetType,
                        nvdbSearchDistance: this.props.nvdbSearchDistance
                    });
                    break;

                case "NVDB_TO_PROJECT":
                    let childType = this.props.featureTypes.find((type) => {
                        return type.typeid == this.props.associationTypeId;
                    });
                    this.setState({
                        showModal: true,
                        associationType: this.props.tab,
                        parentType: this.props.targetType,
                        childType: childType ? ({typeId: childType.typeid, name: childType.name, sortNo: childType.sortNum}) : undefined,
                        nvdbSearchDistance: this.props.nvdbSearchDistance
                    });
                    break;
            }
        } else {
            this.setState({showModal: true});
        }
    }

    associate() {
        if (this.props.autoAssociateCount == 0 || !!this.props.autoAssociateCount) {
            this.props.clearAutoAssociationFeedback();
        }
        let associationParams: AutomaticAssociationParameters = {
            scope: this.state.associationType as ServerAssociationScope,
            parentFeatureTypeId: this.state.parentType.typeId,
            childFeatureTypeId: this.state.childType.typeId,
            maxDistance: this.state.nvdbSearchDistance,
            proximityBasis: this.state.proximityType as ProximityBasis
        };
        this.props.onAutoAssociate(associationParams);
    }

    findFirstWithId(array: SkinnyFeatureType[], id: number): SkinnyFeatureType {
        return array.find(featureType => featureType.typeId === id);
    }

    getDistinctFeatureTypes(value: SkinnyFeatureType, index: number, self: SkinnyFeatureType[]): boolean {
        return self.indexOf(this.findFirstWithId(self, value.typeId)) === index;
    }

    getParentTypeOptions(associationType: String): SkinnyFeatureType[] {
        switch (associationType) {
            case "EMPTY":
                return [];
            case "PROJECT_TO_PROJECT":
            case "PROJECT_TO_NVDB":
                return this.props.featureTypes.map(type => {
                    return {typeId: type.typeid, name: type.name, sortNo: type.sortNum};
                });
            case "NVDB_TO_PROJECT":
                return this.props.featureTypes
                    .map(type => type.parentTypes)
                    .reduce((accumulator, current) => accumulator.concat(current))      // flatten arrays
                    .filter((value, index, self) => this.getDistinctFeatureTypes(value, index, self));
        }
    }

    getChildTypeOptions(associationType: String, parentType: SkinnyFeatureType): SkinnyFeatureType[] {
        if (parentType.name == "EMPTY") {
            return [];
        }
        switch (associationType) {
            case "EMPTY":
                return [];
            case "PROJECT_TO_PROJECT":
                return this.getFeatureTypeSummary(parentType.name)
                    .childTypes
                    .filter(this.featureTypeExistsInProject);

            case "PROJECT_TO_NVDB":
                return this.getFeatureTypeSummary(parentType.name).childTypes;
            case "NVDB_TO_PROJECT":
                let allowedChildrenFilter = this.generateAllowedChildrenFilter(parentType);
                return this.props.featureTypes.filter(allowedChildrenFilter)
                    .map(type => {
                        return {typeId: type.typeid, name: type.name, sortNo: type.sortNum}
                    });
        }
    }

    featureTypeExistsInProject = (featureType: SkinnyFeatureType): boolean => {
        return this.props.featureTypes.some(type => type.name == featureType.name);
    };

    generateAllowedChildrenFilter(parentType: SkinnyFeatureType): (child: FeatureTypeSummary) => boolean {
        return (child: FeatureTypeSummary): boolean => {
            return child.parentTypes.some(type => type.name === parentType.name);
        }
    }

    getFeatureTypeSummary(featureTypeName: String): FeatureTypeSummary {
        return this.props.featureTypes.find(type => type.name == featureTypeName);
    }

    handleAssociationTypeChange(event: React.ChangeEvent<HTMLSelectElement>) {
        this.setState({
            associationType: event.target.value,
            parentType: {} as SkinnyFeatureType,
            childType: {} as SkinnyFeatureType,
            parentOptions: this.getParentTypeOptions(event.target.value),
            childOptions: []
        });
    }

    handleParentTypeChange(event: React.ChangeEvent<HTMLSelectElement>) {
        let typeName = event.target.value;
        let parentType = this.getParentType(typeName);
        this.setState(
            {
                parentType: parentType,
                childOptions: this.getChildTypeOptions(this.state.associationType, parentType)
            });
    }

    handleChildTypeChange(event: React.ChangeEvent<HTMLSelectElement>) {
        let typeName = event.target.value;
        this.setState({
            childType: this.getChildType(typeName)
        });
    }

    getParentType(typeName: string) {
        return this.state.parentOptions.find(parent => parent.name === typeName);
    }

    getChildType(typeName: string) {
        return this.state.childOptions.find(child => child.name === typeName);
    }

    handleMaxDistanceChange(event: React.ChangeEvent<HTMLInputElement>) {
        let distance = event.target.value;
        this.setState({nvdbSearchDistance: Number(distance)});
    }

    handleProximityChange(event: React.ChangeEvent<HTMLSelectElement>) {
        let proximityType = event.target.value;
        this.setState({proximityType: proximityType});
    }

    render() {
        let title = "Automatisk koble vegobjekter";
        let {associationType, parentOptions, childOptions} = this.state;


        let footer =
            <ModalFooter>
                {this.props.isFetching && <span className="inline-spinner-orange" style={{"marginLeft": "100px"}}/>}

                {this.props.autoAssociateCount != -1 && <span
                    className="auto-association-result">{this.props.autoAssociateCount} {this.props.autoAssociateCount === 1 ? "objekt" : "objekter"} ble koblet sammen</span>}

                <Button bsStyle="default" onClick={(e) => this.close()}>Lukk</Button>
                <Button bsStyle="default" onClick={(e) => this.associate()}
                        disabled={this.state.associationType == "EMPTY"
                        || this.state.parentType.name == "EMPTY"
                        || this.state.childType.name == "EMPTY"}
                >Start automatisk sammenkobling</Button>
            </ModalFooter>;

        let body = (
            <ModalBody className="autoassociate-modal">
                <form>
                    <select onChange={(e) => this.handleAssociationTypeChange(e)}>
                        <option value="EMPTY" hidden selected>Velg sammensetning</option>
                        <option value="PROJECT_TO_PROJECT">Både mor- og datterobjekt i leveranse</option>
                        <option value="PROJECT_TO_NVDB">Morobjekt i leveranse - datterobjekt i NVDB</option>
                        <option value="NVDB_TO_PROJECT">Morobjekt i NVDB - datterobjekt i leveranse</option>
                    </select>

                    <select onChange={(e) => this.handleParentTypeChange(e)}>
                        <option value="EMPTY" hidden key="vm">Velg morobjekttype</option>
                        {
                            parentOptions.map(featureType => {
                                return (
                                    <option key={featureType.typeId}
                                            value={featureType.name}>{featureType.name}</option>
                                )
                            })
                        }
                    </select>

                    <select onChange={(e) => this.handleChildTypeChange(e)}>
                        <option value="EMPTY" hidden key="vd">Velg datterobjekttype</option>
                        {
                            childOptions.map(featureType => {
                                return (
                                    <option key={featureType.typeId}
                                            value={featureType.name}>{featureType.name}</option>
                                )
                            })}
                    </select>

                    <div style={{display: "flex", width: "90%"}}>

                        <div style={{flexGrow: 1}}>
                            <input type="number" value={this.state.nvdbSearchDistance}
                                   onChange={(e) => this.handleMaxDistanceChange(e)} style={{width: "70%"}}/> m
                        </div>
                        <div style={{flexGrow: 10}}>
                            <select value={this.state.proximityType}
                                    onChange={(e) => this.handleProximityChange(e)}
                                    style={{width: "100%"}}>
                                <option value="GEOMETRY">Geometri</option>
                                <option value="LOCATIONAL">Vegnett</option>
                            </select>
                        </div>
                    </div>
                </form>

                {associationType && <div className="infobox">Mødre som kobles vil bli importert inn i kontrakten.</div>}

            </ModalBody>
        );


        return (

            <ErrorBoundary>
                <div>
                    <Button className="autokobling" onClick={() => this.open()}>Automatisk sammenkobling</Button>
                    {
                        this.state.showModal &&
                        <Modal show={true} onHide={() => this.close()}>
                            <ModalHeader>
                                <ModalTitle>{title}</ModalTitle>
                            </ModalHeader>
                            {body}
                            {footer}
                        </Modal>
                    }
                </div>
            </ErrorBoundary>
        )

    }
}